import PropTypes from 'prop-types';
import FormRow from '@/components/core/form/FormRow';
import { useFormContext } from 'react-hook-form';
import get from 'just-safe-get';
import ErrorMessage from './ErrorMessage';
import ConversionField from '../ConversionField';
import { isInteger, isNumber } from '@/utils/miscUtils';
import usePatchParams from '../usePatchParams';
import { useParamsContext } from '@/components/providers/useParamsContext';
import SelectBase from '@/components/core/form/SelectBase';
import { DISPLAY_TYPES } from '@/consts';

const setValueAs = v => {
  if (isInteger(v)) {
    return parseInt(v);
  }

  if (isNumber(v)) {
    return parseFloat(v);
  }

  return v;
};

const SelectField = ({
  paramSchema,
  name,
  param,
  parentName,
  onChange,
  displayType = DISPLAY_TYPES.row,
  isGroupField = false,
}) => {
  const { label, tooltip, source, convertible_units: convertibleUnits, allow_null: allowNull, editable } = paramSchema;
  const { primary: { value, is_default: isDefault } = {}, unit, options = [] } = param ?? {};
  const { readOnly } = useParamsContext();
  const disabled = !editable || readOnly;
  const {
    formState: { errors },
    setValue,
  } = useFormContext();

  const error = get(errors, name);
  const errorMessage = isGroupField
    ? errors?.[parentName]?.message?.[paramSchema.name]?.primary?.value
    : error?.message?.primary?.value;
  const { patchPrimary } = usePatchParams();

  const { register } = useFormContext();

  const registration = register(name, {
    setValueAs,
    onChange: async e => {
      const convertedValue = setValueAs(e.target.value);
      if (isGroupField && onChange) {
        setValue(name, convertedValue, { shouldValidate: true, shouldDirty: true });
        onChange(e);
      } else {
        await patchPrimary(name, convertedValue);
      }
    },
    disabled,
  });

  const conversionName = parentName ? `${parentName}.conversion.${name}` : `conversion.${name}`;

  if (displayType === DISPLAY_TYPES.table) {
    return (
      <>
        <tr>
          <td className="text-sm p-2 pl-4">{label}</td>
          <td className="p-2">
            <SelectBase
              {...registration}
              allowNull={allowNull}
              hasError={!!errorMessage}
              isDefault={isDefault}
              value={value}
              options={options}
              disabled={disabled}
            />
          </td>
          <td className="p-2 pr-4 w-full">
            {convertibleUnits?.length > 0 && (
              <ConversionField
                className="w-full"
                unit={unit}
                name={conversionName}
                paramName={name}
                units={convertibleUnits}
                disabled={disabled}
                readOnly={readOnly}
              />
            )}
          </td>
        </tr>
        {errorMessage && (
          <tr>
            <td></td>
            <td className="p-0 pl-1">
              <ErrorMessage text={errorMessage} className="p-0" />
            </td>
            <td></td>
          </tr>
        )}
      </>
    );
  }

  return (
    <FormRow label={label} name={name} tooltip={tooltip} source={source}>
      <div className="flex flex-col flex-1">
        <div className={`${convertibleUnits?.length > 0 ? 'join flex' : ''}`}>
          <SelectBase
            {...registration}
            allowNull={allowNull}
            hasError={!!errorMessage}
            isDefault={isDefault}
            value={value}
            options={options}
            disabled={disabled}
          />
          {convertibleUnits?.length > 0 && (
            <ConversionField
              unit={unit}
              name={conversionName}
              paramName={name}
              units={convertibleUnits}
              disabled={disabled}
              readOnly={readOnly}
            />
          )}
        </div>
        <ErrorMessage text={errorMessage} />
      </div>
    </FormRow>
  );
};

SelectField.propTypes = {
  paramSchema: PropTypes.object,
  param: PropTypes.object,
  name: PropTypes.string,
  parentName: PropTypes.string,
  onChange: PropTypes.func,
  displayType: PropTypes.oneOf(['row', 'table']),
  isGroupField: PropTypes.bool,
  prefix: PropTypes.string,
  watchedFields: PropTypes.object,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SelectField;
